import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
// =============================================
// =============================================

//  this file is in charge of coordinating the
//  dispatching of actions necessary to interact
//  with quizzes via the api for taking purposes

// =============================================
// =============================================

import React from 'react';
import partial from 'lodash/partial';
import { Map } from 'immutable';
import { camelizeKeys } from 'humps';
import t from '@instructure/quiz-i18n/format-message';
import noop from '../../common/util/noop';
import { addInfo, screenreaderNotification } from '../../common/actions/alerts';
import Item from '../../common/records/Item';
import { featureOn } from '../../common/util/featureCheck';
import { fetchLogger } from '../../common/kinesis/QuizEventsLogger';
import { POLITE, STUDENT_ACCESS_CODE_STATUS } from '@instructure/quiz-common';
import * as uiActions from '../../common/actions/ui';
import { fetchQuizSession, fetchResumeData, fetchTime, recordTime, singleResponseSubmit, submitQuizSession, submitStudentAccessCode as submitStudentAccessCodeApi, takeQuizSession } from '../../common/api/quizSessions';
import { getSessionItems, handleQuizSessionFetch } from '../../common/actions/quizSessions';
import { handleSessionItemsResponse, handleResumeDataResponse, handleQuizSessionResponse } from '../../common/api/callHandlers';
import * as modalActions from '../../common/actions/modal';
import { backtrackingNextQuestion, sessionStartOrResumeEvent, setCurrentSessionItemPosition, submitQuizSessionEvent, updateQuizSubmitFlag, updateQuizSessionStatusOnPageLoad, updateTimerInfo } from '../../common/actions/taking';
import { getClientIpAddress } from '../../common/util/getClientIpAddress';
var getQuizSessionConfig = function getQuizSessionConfig(quizSession) {
  // these should be handled by existing functions
  var oneQuestionAtATime = quizSession.isOneQuestionAtATime();
  var allowBacktracking = quizSession.allowBacktracking;
  var useSessionItemIndex = !oneQuestionAtATime || allowBacktracking;
  return {
    oneQuestionAtATime: oneQuestionAtATime,
    allowBacktracking: allowBacktracking,
    useSessionItemIndex: useSessionItemIndex
  };
};
export var getResumeData = function getResumeData(quizSessionId) {
  return function (dispatch) {
    var responseCb = function responseCb(resumeData) {
      return dispatch(handleResumeDataResponse(resumeData, quizSessionId));
    };
    return fetchResumeData(quizSessionId, responseCb);
  };
};
export var makeSessionItemCall = function makeSessionItemCall(quizSession) {
  var index = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : getSessionItems;
  var next = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : nextQuestion;
  return function (dispatch) {
    var action = getQuizSessionConfig(quizSession).useSessionItemIndex ? index(quizSession.id) : next(quizSession.id, false, false, null, null, null);
    return dispatch(action);
  };
};
export var submitStudentAccessCode = function submitStudentAccessCode(quizSessionId, accessCode) {
  return function (dispatch) {
    var responseCb = function responseCb(sessionData) {
      dispatch(uiActions.set(STUDENT_ACCESS_CODE_STATUS, sessionData.accepted_student_access_code));
      if (sessionData.accepted_student_access_code) {
        dispatch(screenreaderNotification(t('Access code successful. You may now begin this assessment'), POLITE));
      } else {
        dispatch(screenreaderNotification(t('Invalid access code')));
      }
      return dispatch(handleQuizSessionResponse(sessionData, quizSessionId));
    };
    return submitStudentAccessCodeApi(quizSessionId, accessCode, responseCb);
  };
};

// ====================================
// OQAAT no backtracking call
// ====================================
var noBacktrackingNextQuestion = function noBacktrackingNextQuestion(quizSessionId, itemId, position, userResponse, largeFormatResponse, submit) {
  var isPassage = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : false;
  return function (dispatch) {
    var params = {};
    if (itemId || isPassage) {
      params.body = JSON.stringify({
        sessionItemResponse: {
          itemId: itemId,
          position: position,
          userResponse: userResponse,
          largeFormatResponse: largeFormatResponse
        }
      });
    }
    var responseCb = function responseCb(nextItem) {
      if (nextItem) {
        dispatch([handleSessionItemsResponse([nextItem], quizSessionId), setCurrentSessionItemPosition(nextItem.position), getResumeData(quizSessionId)]);
      } else {
        submit();
      }
    };
    var errorCb = function errorCb(errorCode) {
      if (errorCode === 400) {
        return singleResponseSubmit(quizSessionId, {}, responseCb);
      }
    };
    return singleResponseSubmit(quizSessionId, params, responseCb, errorCb);
  };
};

// ====================================
// OQAAT no backtracking call
// ====================================
export var nextQuestion = function nextQuestion(quizSessionId, allowBacktracking, currentQuestionIsLast, position, itemId, userResponse, largeFormatResponse) {
  var submit = arguments.length > 7 && arguments[7] !== undefined ? arguments[7] : submitQuiz;
  var isPassage = arguments.length > 8 ? arguments[8] : undefined;
  if (allowBacktracking) {
    return function (dispatch) {
      var action = currentQuestionIsLast ? submit(quizSessionId) : backtrackingNextQuestion();
      if (action) dispatch(action);
    };
  } else {
    return noBacktrackingNextQuestion(quizSessionId, itemId, position, userResponse, largeFormatResponse, submit, isPassage);
  }
};

// ====================================
// load up the quiz session for the taking page
// ====================================
export function confirmSessionStartPage(quizSessionId) {
  var cb = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : noop;
  var authRedirectCb = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : noop;
  return function (dispatch) {
    return fetchQuizSession(quizSessionId, partial(handleQuizSessionFetch, dispatch)).then(cb)["catch"](function (e) {
      if (e.response && e.response.status === 403) {
        authRedirectCb();
      }
    });
  };
}

// ====================================
// fetch the data for a new quiz_session
// ====================================
export function newQuizTakingSession(quizSessionId) {
  var cb = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : noop;
  var authRedirectCb = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : noop;
  return function (dispatch) {
    return takeQuizSession(quizSessionId, partial(handleQuizSessionFetch, dispatch)).then(cb)["catch"](function (e) {
      if (e.response.status === 403) {
        authRedirectCb();
      }
    });
  };
}
export function onQuizSessionReadyForTaking(quizSession) {
  return /*#__PURE__*/function () {
    var _ref = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(dispatch) {
      var ipAddress;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return getClientIpAddress(quizSession.id);
          case 2:
            ipAddress = _context.sent;
            dispatch([makeSessionItemCall(quizSession), getResumeData(quizSession.id), sessionStartOrResumeEvent(navigator.userAgent, ipAddress), updateQuizSessionStatusOnPageLoad(quizSession.status)]);
          case 4:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return function (_x) {
      return _ref.apply(this, arguments);
    };
  }();
}

// ====================================
// show submit confirmation modal
// ====================================

export var messageForSubmitModal = function messageForSubmitModal(positions) {
  if (!positions || positions.length === 0) {
    return t('Upon submission you will not be able to change your answers. Are you ready to submit?');
  }
  if (positions.length >= 10) {
    return featureOn('invalid_input_warning') ? t('Multiple questions have either not been answered or have invalid answers. Upon submission you will not be able to change your answers. Are you ready to submit?') : t('Multiple questions have not been answered. Upon submission you will not be able to change your answers. Are you ready to submit?');
  }
  var message = featureOn('invalid_input_warning') ? t('The following questions have either not been answered or have invalid answers:') : t('The following questions have not been answered:');
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("p", null, message), /*#__PURE__*/React.createElement("ul", null, positions.map(function (position) {
    return /*#__PURE__*/React.createElement("li", {
      key: position
    }, t('Question { position, number }', {
      position: position
    }));
  })), /*#__PURE__*/React.createElement("p", null, t('Upon submission you will not be able to change your answers. Are you ready to submit?')));
};
var __getResponseForSessionItem = function __getResponseForSessionItem(sessionItem, responses) {
  var resp = responses.find(function (r) {
    return r.get('position') === sessionItem.get('position');
  });
  if (featureOn('invalid_input_warning') && resp && resp.get('invalid')) {
    return null;
  }
  return resp && resp.get('userResponse').toJS();
};
var __remainingQuestionPositions = function __remainingQuestionPositions(sessionItems, responses) {
  return sessionItems.toList().sortBy(function (si) {
    return si.get('position');
  }).filterNot(function (si) {
    var item = si.get('item');
    var interactionType = item.get('interactionType');
    return typeof interactionType === 'undefined' || interactionType === 'no-interaction';
  }).filterNot(function (si) {
    var item = new Item(si.get('item'));
    var itemUserResponse = __getResponseForSessionItem(si, responses);
    return item.hasResponse(itemUserResponse);
  }).map(function (si) {
    return si.get('questionNumber');
  }).toSet().toJS();
};
export function showSubmitConfirmModal(quizSessionId) {
  var onSubmitQuizSession = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : noop;
  return function (dispatch, getState) {
    var needResponse = getQuestionsNeedingAResponse(getState(), quizSessionId);
    var title = titleForSubmitModal(needResponse);
    var message = messageForSubmitModal(needResponse);
    var modalAction = modalActions.withConfirm(function () {
      return dispatch(postQuizSession(quizSessionId, onSubmitQuizSession));
    }, {
      title: title,
      text: message,
      continueText: t('Submit'),
      cancelText: t('Cancel')
    });
    return dispatch(modalAction);
  };
}
export function closeSubmitConfirmModal(quizSessionId) {
  return function (dispatch, getState) {
    var needResponse = getQuestionsNeedingAResponse(getState(), quizSessionId);
    var title = titleForSubmitModal(needResponse);
    return dispatch(modalActions.closeModal(title));
  };
}
function titleForSubmitModal(needResponse) {
  return needResponse.length > 0 ? t('Unanswered Questions!') : t('Confirm Submission');
}

// ==============================================
// check for unanswered questions or override
// ==============================================

export function submitQuiz(quizSessionId) {
  var onSubmitQuizSession = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : noop;
  var overrideConfirmation = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  return function (dispatch) {
    if (overrideConfirmation) {
      return dispatch(postQuizSession(quizSessionId, onSubmitQuizSession));
    } else {
      return dispatch(showSubmitConfirmModal(quizSessionId, onSubmitQuizSession));
    }
  };
}
export function notifyTimeAdded(isUnlimited, extraTime) {
  return function (dispatch) {
    var message = isUnlimited ? t('Your original time limit has been extended by { extraTime }', {
      extraTime: extraTime
    }) : t('Unlimited time accommodation has been added to your quiz');
    return dispatch(addInfo(message));
  };
}
export function updateTime(timeInSeconds, timerRunning) {
  return function (dispatch) {
    return dispatch(updateTimerInfo({
      timeLeftInSeconds: timeInSeconds,
      timerRunning: timerRunning,
      timeRecording: false
    }));
  };
}
function getQuestionsNeedingAResponse(state, quizSessionId) {
  var responses = state.getIn(['taking', 'responses'], Map());
  var sessionItems = state.get('sessionItems', Map()).filter(function (record) {
    return record.get('quizSessionId') === quizSessionId;
  });
  return __remainingQuestionPositions(sessionItems, responses);
}

// ====================================
// submit the data for the quiz
// ====================================

export function postQuizSession(quizSessionId) {
  var onSubmitQuizSession = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : noop;
  // send kinesis credentials request before submit quiz when it is still
  //   in quiz.taking scope, we don't mind when responses are coming back (
  //   before/after the scope is switched)
  // after submit quiz response is received, kinesis refresh is forbidden for
  //   quiz.results scope
  fetchLogger(quizSessionId);
  return function (dispatch, getState) {
    var submitFlag = getState().getIn(['taking', 'submitFlag']);
    if (!submitFlag) {
      dispatch(updateQuizSubmitFlag(true));
      var takingResponses = getState().getIn(['taking', 'responses'], Map()).valueSeq().toJS();
      var params = {
        body: JSON.stringify({
          session_item_responses: takingResponses,
          submission_source: "browser_session_id:".concat(getState().getIn(['taking', 'browserSessionId']))
        })
      };
      var submitQuizSessionEventAction = submitQuizSessionEvent();
      var responseCb = function responseCb() {
        dispatch(screenreaderNotification(t('Quiz submitted')));
        onSubmitQuizSession();
        dispatch(submitQuizSessionEventAction);
      };
      var errorCb = function errorCb(errorCode) {
        setTimeout(function () {
          dispatch(updateQuizSubmitFlag(false));
        }, 10000);
      };
      return submitQuizSession(quizSessionId, params, responseCb, errorCb);
    }
  };
}

// ====================================
//  getting the remaining time
// ====================================

export function getTimeRemaining(quizSessionId, cb) {
  return function (dispatch) {
    return fetchTime(quizSessionId, function (result) {
      return cb(camelizeKeys(result));
    });
  };
}

// ====================================
//  tracking time taken
// ====================================

export function recordQuizTime(quizSessionId, cb) {
  return function (dispatch) {
    return recordTime(quizSessionId, function (result) {
      return cb(camelizeKeys(result));
    });
  };
}